import * as Sentry from "@sentry/react";
import { initFrontendSentry } from "@vjal/frontend-core";
import { appConfig } from "./config";

interface CaptureContext {
  source: string;
  extra?: Record<string, unknown>;
  tags?: Record<string, string | number | boolean | null | undefined>;
}

interface ErrorWithReference {
  reference?: unknown;
  error_ref?: unknown;
  errorRef?: unknown;
}

const sentryDsn = (import.meta.env.VITE_SENTRY_DSN as string | undefined) ?? "";

let initialized = false;

function parseSampleRate(value: string | undefined, fallback: number) {
  if (!value) return fallback;

  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : fallback;
}

export function buildErrorReference(seed?: string | null) {
  const raw = (seed ?? crypto.randomUUID().replace(/-/g, "")).replace(/[^a-zA-Z0-9]/g, "");
  const suffix = raw.slice(-8).toUpperCase().padStart(8, "0");
  return `CD-${suffix}`;
}

export function getErrorReference(error: unknown): string | null {
  if (!error || typeof error !== "object") return null;

  const candidate = error as ErrorWithReference;
  const reference = candidate.errorRef ?? candidate.error_ref ?? candidate.reference;
  return typeof reference === "string" && reference.trim() ? reference : null;
}

export function isObservabilityEnabled() {
  return Boolean(sentryDsn);
}

export function initObservability() {
  if (initialized || !isObservabilityEnabled()) return;

  initialized = initFrontendSentry({
    sentry: Sentry,
    dsn: sentryDsn,
    environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) || import.meta.env.MODE,
    release: (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) || undefined,
    sendDefaultPii: false,
    tracesSampleRate: parseSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as string | undefined, 0),
    integrations: [Sentry.browserTracingIntegration()],
    tags: {
      app: "coaching-dashboard",
      app_code: "CD",
      data_mode: appConfig.dataMode,
      auth_mode: appConfig.authMode,
    }
  });
}

export function captureError(error: unknown, context: CaptureContext) {
  const existingReference = getErrorReference(error);
  if (!isObservabilityEnabled()) return existingReference ?? buildErrorReference();

  let eventId: string | null = null;

  Sentry.withScope((scope) => {
    scope.setTag("source", context.source);
    Object.entries(context.tags ?? {}).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      scope.setTag(key, String(value));
    });

    if (context.extra) scope.setContext("details", context.extra);
    eventId = Sentry.captureException(error);
  });

  return existingReference ?? buildErrorReference(eventId);
}
